import { useState, useCallback, useEffect, useRef } from 'react';
import type { Size, Position } from '../types';
import { TASKBAR_HEIGHT } from '../utils/constants';

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface UseResizableOptions {
  initialSize: Size;
  minSize: Size;
  position: Position;
  onResizeStart?: () => void;
  onResize?: (size: Size, position: Position) => void;
  onResizeEnd?: (size: Size, position: Position) => void;
  disabled?: boolean;
}

interface UseResizableReturn {
  size: Size;
  resizedPosition: Position;
  isResizing: boolean;
  resizeHandleProps: (direction: ResizeDirection) => {
    onMouseDown: (e: React.MouseEvent) => void;
    onTouchStart: (e: React.TouchEvent) => void;
    style: React.CSSProperties;
  };
  setSize: (size: Size) => void;
}

const HANDLE_SIZE = 10;

const getCursorForDirection = (direction: ResizeDirection): string => {
  const cursors: Record<ResizeDirection, string> = {
    n: 'n-resize',
    s: 's-resize',
    e: 'e-resize',
    w: 'w-resize',
    ne: 'ne-resize',
    nw: 'nw-resize',
    se: 'se-resize',
    sw: 'sw-resize',
  };
  return cursors[direction];
};

const getHandleStyle = (direction: ResizeDirection): React.CSSProperties => {
  const base: React.CSSProperties = {
    position: 'absolute',
    zIndex: 10,
  };

  switch (direction) {
    case 'n':
      return { ...base, top: 0, left: HANDLE_SIZE, right: HANDLE_SIZE, height: HANDLE_SIZE, cursor: 'n-resize' };
    case 's':
      return { ...base, bottom: 0, left: HANDLE_SIZE, right: HANDLE_SIZE, height: HANDLE_SIZE, cursor: 's-resize' };
    case 'e':
      return { ...base, right: 0, top: HANDLE_SIZE, bottom: HANDLE_SIZE, width: HANDLE_SIZE, cursor: 'e-resize' };
    case 'w':
      return { ...base, left: 0, top: HANDLE_SIZE, bottom: HANDLE_SIZE, width: HANDLE_SIZE, cursor: 'w-resize' };
    case 'ne':
      return { ...base, top: 0, right: 0, width: HANDLE_SIZE, height: HANDLE_SIZE, cursor: 'ne-resize' };
    case 'nw':
      return { ...base, top: 0, left: 0, width: HANDLE_SIZE, height: HANDLE_SIZE, cursor: 'nw-resize' };
    case 'se':
      return { ...base, bottom: 0, right: 0, width: HANDLE_SIZE, height: HANDLE_SIZE, cursor: 'se-resize' };
    case 'sw':
      return { ...base, bottom: 0, left: 0, width: HANDLE_SIZE, height: HANDLE_SIZE, cursor: 'sw-resize' };
  }
};

export const useResizable = ({
  initialSize,
  minSize,
  position,
  onResizeStart,
  onResize,
  onResizeEnd,
  disabled = false,
}: UseResizableOptions): UseResizableReturn => {
  const [size, setSize] = useState<Size>(initialSize);
  const [resizedPosition, setResizedPosition] = useState<Position>(position);
  const [isResizing, setIsResizing] = useState(false);

  const resizeRef = useRef<{
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
    startPosX: number;
    startPosY: number;
    direction: ResizeDirection;
  } | null>(null);

  // Update size when initialSize changes externally
  useEffect(() => {
    setSize(initialSize);
  }, [initialSize]);

  useEffect(() => {
    setResizedPosition(position);
  }, [position]);

  const handleMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!resizeRef.current) return;

      const { startX, startY, startWidth, startHeight, startPosX, startPosY, direction } = resizeRef.current;

      const deltaX = clientX - startX;
      const deltaY = clientY - startY;

      let newWidth = startWidth;
      let newHeight = startHeight;
      let newX = startPosX;
      let newY = startPosY;

      // Calculate new dimensions based on direction
      if (direction.includes('e')) {
        newWidth = Math.max(minSize.width, startWidth + deltaX);
      }
      if (direction.includes('w')) {
        const widthDelta = Math.min(deltaX, startWidth - minSize.width);
        newWidth = startWidth - widthDelta;
        newX = startPosX + widthDelta;
      }
      if (direction.includes('s')) {
        newHeight = Math.max(minSize.height, startHeight + deltaY);
      }
      if (direction.includes('n')) {
        const heightDelta = Math.min(deltaY, startHeight - minSize.height);
        newHeight = startHeight - heightDelta;
        newY = startPosY + heightDelta;
      }

      // Constrain to screen bounds
      const maxWidth = window.innerWidth - newX;
      const maxHeight = window.innerHeight - TASKBAR_HEIGHT - newY;

      newWidth = Math.min(newWidth, maxWidth);
      newHeight = Math.min(newHeight, maxHeight);

      // Ensure position doesn't go negative
      if (newX < 0) {
        newWidth += newX;
        newX = 0;
      }
      if (newY < 0) {
        newHeight += newY;
        newY = 0;
      }

      const newSize = { width: newWidth, height: newHeight };
      const newPosition = { x: newX, y: newY };

      setSize(newSize);
      setResizedPosition(newPosition);
      onResize?.(newSize, newPosition);
    },
    [minSize, onResize]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      handleMove(e.clientX, e.clientY);
    },
    [handleMove]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (e.touches.length === 1) {
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    },
    [handleMove]
  );

  const handleEnd = useCallback(() => {
    if (resizeRef.current) {
      setIsResizing(false);
      document.body.classList.remove('dragging');
      document.body.style.cursor = '';
      onResizeEnd?.(size, resizedPosition);
      resizeRef.current = null;
    }
  }, [onResizeEnd, size, resizedPosition]);

  // Add global listeners when resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
      document.addEventListener('touchcancel', handleEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleEnd);
        document.removeEventListener('touchcancel', handleEnd);
      };
    }
  }, [isResizing, handleMouseMove, handleTouchMove, handleEnd]);

  const startResize = useCallback(
    (clientX: number, clientY: number, direction: ResizeDirection) => {
      if (disabled) return;

      resizeRef.current = {
        startX: clientX,
        startY: clientY,
        startWidth: size.width,
        startHeight: size.height,
        startPosX: resizedPosition.x,
        startPosY: resizedPosition.y,
        direction,
      };

      setIsResizing(true);
      document.body.classList.add('dragging');
      document.body.style.cursor = getCursorForDirection(direction);
      onResizeStart?.();
    },
    [disabled, size, resizedPosition, onResizeStart]
  );

  const resizeHandleProps = useCallback(
    (direction: ResizeDirection) => ({
      onMouseDown: (e: React.MouseEvent) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        startResize(e.clientX, e.clientY, direction);
      },
      onTouchStart: (e: React.TouchEvent) => {
        if (e.touches.length === 1) {
          e.stopPropagation();
          startResize(e.touches[0].clientX, e.touches[0].clientY, direction);
        }
      },
      style: getHandleStyle(direction),
    }),
    [startResize]
  );

  return {
    size,
    resizedPosition,
    isResizing,
    resizeHandleProps,
    setSize,
  };
};
